import React, { useState, useEffect, useReducer, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import type { SortingState } from '@tanstack/react-table';

import { useReadOnly } from 'hooks/read-only';

import Loader from 'components/partials/loader/loader';
import FlashNotification from 'components/partials/flash-notification/flash-notification';
import TableComponent from 'components/partials/table-component/table-component';
import TabHeader from 'components/partials/tab-header';
import Notification from 'components/partials/notification/notification.helper';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';

import { usersReducer } from 'containers/admin/reducer';
import { getUsersTableSettings } from 'containers/admin/users/users-table-settings';
import type { UsersTableRow } from 'containers/admin/users/users-table-settings';
import { PAGE_CHANGE, SEARCH_CHANGE } from 'containers/admin/actions';

import { useImpersonation } from 'hooks/use-impersonation';
import { useUsersList } from 'hooks/api/use-users';

import Icons from 'assets/icons';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';

interface NotificationParams {
  heading: string;
  variant: string;
  message: string;
}

interface UsersAdminPageProps {
  clientId?: string | number | null;
  notificationParams?: NotificationParams | null;
  isTab?: boolean;
}

interface UsersAdminPageState {
  search: string;
  pageIndex: number;
}

interface PaginationInfo {
  pageSize: number;
  totalRecords: number;
}

const initialState: UsersAdminPageState = {
  search: '',
  pageIndex: 0,
};

const UsersAdminPage = ({
  clientId,
  notificationParams,
  isTab,
}: UsersAdminPageProps): React.JSX.Element => {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(usersReducer, initialState);
  const [data, setData] = useState<UsersTableRow[]>([]);
  const [{ pageSize, totalRecords }, setAmount] = useState<PaginationInfo>({
    pageSize: 100,
    totalRecords: 0,
  });

  const { search, pageIndex } = state;

  const impersonation = useImpersonation();
  const readOnly = useReadOnly();

  const columns = getUsersTableSettings({
    clientId,
    impersonation,
  });

  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: 'firstName', desc: false },
  ]);

  const {
    data: usersData,
    pagination,
    loading: isLoading,
    isFetching,
    error: loadingUsersError,
  } = useUsersList({
    pageSize,
    pageIndex,
    sortBy: sortingParams,
    filters: {
      clientId,
      search: { value: state.search },
    },
  });

  useEffect(() => {
    if (usersData.length > 0) {
      const updatedData = usersData.map((user) => {
        if (!user.client) {
          return { ...user, client: { name: '' } };
        }

        return user;
      });

      setData(updatedData as UsersTableRow[]);
    } else {
      setData([]);
    }

    if (pagination) {
      setAmount({ pageSize, totalRecords: pagination.totalRecords ?? 0 });
    }
  }, [usersData, pagination]);

  const handleSearch = useCallback(
    (payload: string) => dispatch({ type: SEARCH_CHANGE, payload }),
    []
  );

  const handlePagination = useCallback(
    (payload: number) => dispatch({ type: PAGE_CHANGE, payload }),
    []
  );

  const handleEmptyLink = (): void => {
    navigate(
      clientId ? `/admin/clients/${clientId}/users/new` : '/admin/users/new'
    );
  };

  useEffect(() => {
    if (loadingUsersError) setData([]);
  }, [loadingUsersError]);

  useEffect(() => {
    if (notificationParams)
      Notification.flash(
        notificationParams.heading,
        notificationParams.variant,
        notificationParams.message
      );
  }, [notificationParams]);

  const renderContent = (): React.JSX.Element => {
    if (isLoading) {
      return (
        <Loader
          loading={true}
          fullScreen={false}
          customClass={isTab ? '!bg-white' : undefined}
          overlayClassName={isTab ? '!opacity-0' : undefined}
        />
      );
    }

    if (data.length > 0 || isFetching) {
      return (
        <div className="relative">
          <TableComponent
            columns={columns}
            data={data}
            pageIndex={pageIndex}
            pageSize={pageSize}
            setPagination={handlePagination}
            setAmount={setAmount}
            sortingParams={sortingParams}
            setSortingParams={setSortingParams}
            totalRecords={totalRecords}
            isLoading={isFetching}
          />
        </div>
      );
    }

    return (
      <EmptySpaceContent
        customClass="flex-1"
        image={
          clientId ? (
            <Icons.EmptyImage customClass="size-20" />
          ) : (
            <Icons.EmptyImageAll customClass="size-20" />
          )
        }
        text={
          clientId
            ? 'This account has no users yet.'
            : 'Sorry, no results found. Please try a different keyword.'
        }
        linkShow={clientId ? !readOnly : null}
        linkText={clientId ? 'Add a new user' : null}
        onClick={handleEmptyLink}
      />
    );
  };

  return (
    <ResponsiveWrapper
      customClass={classNames('flex flex-col', { '!px-0': !!clientId })}
    >
      <TabHeader
        title="Users"
        headingVariant={clientId ? 'h2' : 'h1'}
        search={search}
        onSearchChange={handleSearch}
        actionButton={
          !readOnly
            ? {
                label: 'Add a User',
                to: clientId
                  ? `/admin/clients/${clientId}/users/new`
                  : '/admin/users/new',
                testId: 'add-user-button',
              }
            : undefined
        }
      />

      <FlashNotification customClass="mb-4" />

      {renderContent()}
    </ResponsiveWrapper>
  );
};

export default UsersAdminPage;
